// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo, useCallback, useEffect, useRef } from 'react';

import type { Manufacturer, ManufacturerId } from '@evolvemep/api-ts';
import { useUncontrolled } from '@mantine/hooks';

import type { CreatableProps } from 'components/Mantine/TypeSafeSelect/Creatables/types';
import type { WrappedSelectProps } from 'components/Mantine/TypeSafeSelect/types';
import { WrappedSelect } from 'components/Mantine/TypeSafeSelect/WrappedSelect';
import { NewManufacturerModal } from 'hooks/typeCacheHooks/NewManufacturerModal';

import { useManufacturers } from './useManufacturers';

type Props = {
  value?: Manufacturer | null;
  onChange: (value: Manufacturer | null) => void;
  defaultToGeneric?: boolean;
} & Omit<WrappedSelectProps<ManufacturerId, object>, 'data' | 'value' | 'onChange' | keyof CreatableProps>;

export const ManufacturerSelect = ({
  value: parentValue,
  onChange: parentOnChange,
  defaultToGeneric,
  ...props
}: Props) => {
  const { manufacturers, loading } = useManufacturers();

  const [value, onChange] = useUncontrolled<Manufacturer | null>({
    value: parentValue,
    onChange: parentOnChange,
  });

  const data = useMemo(
    () =>
      manufacturers?.map((m) => ({
        label: m.name,
        value: m.manufacturerId,
      })) ?? [],
    [manufacturers],
  );

  const wrappedOnChange = useCallback(
    (manufacturerId: ManufacturerId | null) => {
      const manufacturer = manufacturers?.find((m) => m.manufacturerId === manufacturerId);
      onChange(manufacturer ?? value);
    },
    [manufacturers, onChange, value],
  );

  const needToFindGeneric = useRef(defaultToGeneric && !value);
  useEffect(() => {
    if (needToFindGeneric.current) {
      const genericUom = manufacturers?.find((m) => m.name === 'Generic');
      if (genericUom) {
        onChange(genericUom);
        needToFindGeneric.current = false;
      }
    }
  }, [manufacturers, onChange]);

  return (
    <WrappedSelect
      label="Manufacturer"
      searchable
      maxLength={64}
      creatable
      createLabel="Create new manufacturer"
      creationModal={(defaultValue, modalProps) => (
        <NewManufacturerModal manufacturerName={defaultValue} onCreate={onChange} {...modalProps} />
      )}
      loading={loading}
      {...props}
      value={value?.manufacturerId ?? null}
      onChange={wrappedOnChange}
      data={data}
    />
  );
};
